import { browser } from '$app/env';
import { getFromLocalStorage, writeToLocalStorage } from '#src/util/local-storage-sync.svelte.ts';
import { getContext, setContext } from 'svelte';

const MOBILE_BREAKPOINT = 768; // Width below which panels collapse
const STORAGE_KEY_SIDENAV = 'layout.sideNavExpanded';

export class LayoutContext {
	isSmallScreen = $state(browser ? window.innerWidth < MOBILE_BREAKPOINT : true);
	scrollContainer: HTMLElement | null = $state(null);

	private _sideNavExpanded = $state(this.getInitialState());

	get sideNavExpanded() {
		return this._sideNavExpanded;
	}

	constructor() {
		if (browser) {
			$effect(() => {
				const handleResize = () => {
					this._sideNavExpanded = this.getExpandedState();
				};
				window.addEventListener('resize', handleResize);
				return () => window.removeEventListener('resize', handleResize);
			});
		}
	}

	private getInitialState() {
		if (!browser) {
			return false;
		}

		const isSmallScreen = window.innerWidth < MOBILE_BREAKPOINT;
		if (isSmallScreen) {
			return false;
		}

		return getFromLocalStorage(STORAGE_KEY_SIDENAV, true);
	}

	private getExpandedState(): boolean {
		const isSmallScreen = window.innerWidth < MOBILE_BREAKPOINT;
		const wasSmallScreen = this.isSmallScreen;
		this.isSmallScreen = isSmallScreen;

		// Collapse on mobile if shrunk below breakpoint
		if (isSmallScreen && !wasSmallScreen) {
			return false;
		}

		if (isSmallScreen === wasSmallScreen) {
			return this._sideNavExpanded;
		}

		// Use saved preferences if available, otherwise default to expanded
		return getFromLocalStorage(STORAGE_KEY_SIDENAV, true);
	}

	setSideNavExpanded(expanded: boolean) {
		this._sideNavExpanded = expanded;
		writeToLocalStorage(STORAGE_KEY_SIDENAV, expanded);
	}

	toggleSidenav() {
		this.setSideNavExpanded(!this.sideNavExpanded);
	}

	closeSideNavIfMobile() {
		if (this.isSmallScreen) {
			this.setSideNavExpanded(false);
		}
	}
}

export const LAYOUT_CONTEXT_KEY = Symbol('layout');

export function getLayoutContext() {
	return getContext<LayoutContext>(LAYOUT_CONTEXT_KEY);
}

export function setLayoutContext(ctx: LayoutContext) {
	setContext(LAYOUT_CONTEXT_KEY, ctx);
}
